import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { CalendarToday, PersonPin } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { CalendarCommands } from "../../stores/Calendar/calendar.commands";
import { PatientCommands } from "../../stores/Patients/patient.commands";
import useStore from "../../helpers/useStore";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { PatientDto } from "../../api-client";
import { PATIENT_SUBTABS } from "../../stores/Patients/patientsStore";

interface PatientOverviewHeaderProps {
  patient: PatientDto;
  linkOut?: "patientPage" | "calendar";
}

/** Base name size — a touch below h6 (20px), which was too wide for the drawer. */
const NAME_MAX_PX = 16.8;
/** Legibility floor. Past this the name ellipsizes instead of shrinking further. */
const NAME_MIN_PX = 12;

const PatientOverviewHeader: React.FC<PatientOverviewHeaderProps> = ({ patient, linkOut }) => {
  const navigate = useNavigate();
  const calendarStore = useStore(CalendarContext);
  const patientsStore = useStore(PatientsContext);

  const { navigateToCalendar } = CalendarCommands(calendarStore);
  const { navigateToPatient } = PatientCommands(patientsStore);

  const handleCalendarNavigation = () => {
    if (!patient?.id) return;
    
    
    // Navigate to calendar with patient context (both target and selected)
    navigateToCalendar(navigate, {
      patientId: patient.id,
      patientLastName: patient.lastName,
      // Optionally set a specific date, e.g., today
      // date: new Date().toISOString().split('T')[0]
    });
  };

  const handlePatientPageNavigation = () => {
    if (!patient?.id) return;
    
    // Navigate to patient page with this patient selected
    navigateToPatient(navigate, patient.id, null, PATIENT_SUBTABS.PRESCRIPTIONS);
  };

  const renderLinkIcon = () => {
    if (!linkOut) return null;

    switch (linkOut) {
      case "calendar":
        return (
          <Tooltip title="Zum Kalender wechseln">
            <IconButton 
              onClick={handleCalendarNavigation}
              size="small"
              sx={{ ml: 1, flexShrink: 0 }}
            >
              <CalendarToday />
            </IconButton>
          </Tooltip>
        );
      case "patientPage":
        return (
          <Tooltip title="Zur Patientenseite wechseln">
            <IconButton 
              onClick={handlePatientPageNavigation}
              size="small"
              sx={{ ml: 1, flexShrink: 0 }}
            >
              <PersonPin />
            </IconButton>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const name = `${patient?.lastName}, ${patient?.firstName}`;

  /**
   * Shrink the name to fit its one line.
   *
   * Layout alone can guarantee "never wraps" OR "never clipped", not both:
   * in a 320px drawer the name gets ~173px once the id, the link icon and the
   * card's own menu button have taken their fixed share, and real names like
   * "Becker-Ludwig, Gisela" need ~181px at the base size. So the size adapts.
   *
   * Text width scales linearly with font-size, so the overflow ratio gives the
   * needed size in ONE measurement rather than stepping down a pixel at a
   * time. Written straight to the node — no state, so fitting never costs a
   * render. Deliberately NOT a ResizeObserver: the drawer is a fixed width, so
   * the name is the only input, which keeps this well clear of the
   * observer-feedback loop that the calendar grid had to be guarded against.
   */
  const nameRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) return;
    let cancelled = false;

    const fit = () => {
      if (cancelled || !nameRef.current) return;
      const node = nameRef.current;
      node.style.fontSize = `${NAME_MAX_PX}px`;
      // clientWidth is the space available once the fixed siblings have taken
      // theirs; scrollWidth is what the text actually wants.
      const ratio = node.scrollWidth / node.clientWidth;
      if (ratio > 1) {
        // 0.99 absorbs sub-pixel rounding so we don't land 1px short and
        // ellipsize anyway.
        node.style.fontSize = `${Math.max(NAME_MIN_PX, (NAME_MAX_PX / ratio) * 0.99)}px`;
      }
    };

    fit();
    // Webfont swap changes every measurement, so re-fit once Inter is in.
    document.fonts?.ready.then(fit).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [name]);

  return (
    // Single-line by construction: the name is the ONLY element allowed to
    // flex, so it absorbs every length difference and the row can never wrap.
    // The id and the link icon are fixed-size (flexShrink: 0), so they are
    // never truncated and never pushed to a second line — previously the whole
    // "Name, Vorname: id" string was one h6 Typography, and a long name simply
    // wrapped, changing the drawer's header height per patient.
    <Box display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
      {/* Tooltip carries the full label: below NAME_MIN_PX the name ellipsizes
          rather than shrinking into illegibility, so it stays recoverable. */}
      <Tooltip title={`${name}: ${patient?.id}`}>
        <Typography
          ref={nameRef}
          variant="h6"
          component="div"
          noWrap
          sx={{ minWidth: 0, fontSize: `${NAME_MAX_PX}px`, lineHeight: 1.3 }}
        >
          {name}
        </Typography>
      </Tooltip>
      {/* The id is a lookup token, not part of the name — demoted so it costs
          the name as little width as possible. tabular-nums keeps it from
          jittering as digits change between patients. */}
      <Typography
        component="span"
        sx={{
          flexShrink: 0,
          fontSize: "0.75rem",
          fontWeight: 600,
          opacity: 0.65,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {patient?.id}
      </Typography>
      {renderLinkIcon()}
    </Box>
  );
};

export default PatientOverviewHeader;